import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CalendarX, Download, PartyPopper, RotateCcw, Trophy } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-color.png`;
const LOGO_WHITE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-white.png`;
const STREAK_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_kpl-league/artifacts/89377fcc3ef00973_image.png";

const EMPTY_FORM = {
  name: "", father_name: "", dob: "", gender: "", mobile: "", whatsapp: "", email_id: "", aadhaar: "", district: "", city: "",
  batting_style: "", bowling_style: "", allrounder: "", wicket_keeper: "",
  ranji_year: "", zone_year: "", district_year: "",
  jersey_size: "", jersey_name: "", jersey_no: "", cricheros_link: "", declaration: false,
};

const LEVELS = [["Ranji Trophy", "ranji_year"], ["Zone", "zone_year"], ["District", "district_year"]];
const JERSEY_SIZES = ["S", "M", "L", "XL", "XXL", "XXXL"];

const Confirmation = ({ reg, onEdit }) => (
  <div className="confirm-wrap" data-testid="registration-confirmation">
    <div className="confirm-ball"><Trophy size={44} /></div>
    <h2 className="confirm-title">Thank you, {reg.name.split(" ")[0]}!</h2>
    <p className="confirm-sub"><PartyPopper size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />PLAYER REGISTRATION SUCCESSFUL</p>
    <div className="confirm-id" data-testid="registration-id">REGISTRATION ID · <b>{reg.registration_id}</b></div>
    <div className="confirm-card">
      <h4>KAKINADA PREMIER LEAGUE (KPL) — SEASON 1 · 2026</h4>
      <div className="confirm-line"><span>Player Name</span><b>{reg.name}</b></div>
      <div className="confirm-line"><span>Registration ID</span><b>{reg.registration_id}</b></div>
      <div className="confirm-line"><span>Mobile Number</span><b>{reg.mobile}</b></div>
      <div className="confirm-line"><span>Jersey</span><b>{reg.jersey_name} · #{reg.jersey_no} · {reg.jersey_size}</b></div>
    </div>
    <p className="confirm-note"><b>What's next?</b> Updates about player selection, trials, and auction details will be shared on your registered mobile number. <b>Note:</b> registration confirmation does not guarantee selection for the tournament.</p>
    <p className="confirm-tagline">YOUR GAME. YOUR DREAM. YOUR KPL!</p>
    {reg.email_sent && <p className="confirm-note" data-testid="confirmation-email-note" style={{ marginTop: -8 }}>A confirmation email has been sent to your registered email address.</p>}
    <div className="confirm-actions">
      <button className="button button-red auth-submit" onClick={() => window.print()} data-testid="confirmation-receipt-button"><Download size={15} /> Download registration receipt</button>
      <a className="button button-outline auth-submit" href="https://www.instagram.com/kakinadapremierleague/" target="_blank" rel="noreferrer" data-testid="confirmation-insta-link">Follow @kakinadapremierleague</a>
      <button className="button button-outline auth-submit" onClick={onEdit} data-testid="confirmation-edit-button">Edit my registration</button>
      <Link className="button button-outline auth-submit" to="/" data-testid="confirmation-home-button">Back to home</Link>
    </div>
  </div>
);

const UploadBox = ({ label, note, value, preview, uploading, onUpload, testid }) => (
  <div className="auth-field"><label>{label}</label>
    <input type="file" accept="image/*,application/pdf" onChange={onUpload} data-testid={testid} />
    {note && <small className="field-note">{note}</small>}
    {uploading && <small className="field-note">Uploading…</small>}
    {value && <small className="field-note upload-ok">Uploaded ✓</small>}
    {preview && <img className="photo-preview" src={preview} alt="preview" />}
  </div>
);

export default function PlayerRegistration() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [existing, setExisting] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(null);
  const [photoPath, setPhotoPath] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [aadhaarPath, setAadhaarPath] = useState("");
  const [dobPath, setDobPath] = useState("");
  const [uploading, setUploading] = useState({ photo: false, aadhaar: false, dob: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [closedAt, setClosedAt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/kpl`).then((r) => {
      const raw = r.data?.registration_close;
      const closeDt = raw ? new Date(raw) : null;
      if (closeDt && !Number.isNaN(closeDt.getTime()) && new Date() > closeDt) setClosedAt(closeDt);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/registrations/mine`).then((r) => {
        if (r.data.registration) {
          const reg = r.data.registration;
          setExisting(reg);
          setForm({ ...EMPTY_FORM, ...reg, declaration: true });
          if (reg.photo_path) { setPhotoPath(reg.photo_path); setPhotoPreview(`${API}/files/${reg.photo_path}`); }
          if (reg.aadhaar_doc_path) setAadhaarPath(reg.aadhaar_doc_path);
          if (reg.dob_doc_path) setDobPath(reg.dob_doc_path);
        }
      }).catch(() => {});
    }
  }, [user]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const doUpload = (endpoint, kind) => async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (kind === "photo") setPhotoPreview(URL.createObjectURL(file));
    setUploading((u) => ({ ...u, [kind]: true }));
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await axios.post(`${API}/registrations/${endpoint}`, body);
      if (kind === "photo") setPhotoPath(data.path);
      if (kind === "aadhaar") setAadhaarPath(data.path);
      if (kind === "dob") setDobPath(data.path);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Upload failed — try again.");
      if (kind === "photo") setPhotoPreview("");
    }
    setUploading((u) => ({ ...u, [kind]: false }));
  };

  const age = form.dob ? Math.max(0, new Date().getFullYear() - new Date(form.dob).getFullYear()) : "";

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    if (!photoPath || !aadhaarPath || !dobPath) {
      setError("Please upload your player photograph, Aadhaar card, and date of birth proof before submitting.");
      setBusy(false);
      return;
    }
    try {
      const { data } = await axios.post(`${API}/registrations`, { ...form, photo_path: photoPath, aadhaar_doc_path: aadhaarPath, dob_doc_path: dobPath });
      setExisting(data.registration);
      setJustSubmitted({ ...data.registration, email_sent: data.email_sent });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  if (user === null) return <div className="admin-loading">Loading…</div>;

  return <div className="auth-wrap">
    <img className="auth-streak" src={STREAK_URL} alt="" aria-hidden="true" />
    <Link className="auth-home" to="/" data-testid="registration-back-home"><ArrowLeft size={15} /> BACK TO SITE</Link>
    <div className="auth-card wide">
      <img className="auth-logo" src={LOGO_URL} alt="KPL logo" />

      {justSubmitted ? <Confirmation reg={justSubmitted} onEdit={() => setJustSubmitted(null)} /> : <>

      {!user ? <div data-testid="registration-login-prompt">
        <h1>Player registration.</h1>
        <p className="auth-sub">You need a KPL account to register as a player. Log in or create one — it takes a minute.</p>
        <Link className="button button-red auth-submit" to="/login" data-testid="registration-login-link">Log in to register</Link>
        <p className="auth-switch">No account yet? <Link to="/register" data-testid="registration-signup-link">Create one</Link></p>
      </div> : (existing && !showForm) ? <div className="status-wrap" data-testid="registration-status">
        <img className="status-logo" src={LOGO_URL} alt="KPL logo" />
        <h2 className="status-title">You're Registered!</h2>
        <p className="status-sub">Kakinada Premier League — Season 1</p>
        <div className="confirm-id status-id" data-testid="status-reg-id">REGISTRATION ID · <b>{existing.registration_id}</b></div>
        <div className="confirm-card status-card">
          <div className="confirm-line"><span>Player Name</span><b>{existing.name}</b></div>
          <div className="confirm-line"><span>Mobile</span><b>{existing.mobile}</b></div>
          <div className="confirm-line"><span>Jersey</span><b>{existing.jersey_name ? `${existing.jersey_name} · #${existing.jersey_no} · ${existing.jersey_size}` : "—"}</b></div>
        </div>
        <p className="confirm-note">Updates about selection, trials, and the auction will be shared on your registered number. Follow <a href="https://www.instagram.com/kakinadapremierleague/" target="_blank" rel="noreferrer">@kakinadapremierleague</a> for news.</p>
        <div className="form-actions">
          {!closedAt && <button className="button button-red auth-submit" onClick={() => setShowForm(true)} data-testid="status-edit-button">Edit my registration</button>}
          <button className="button button-outline auth-submit" onClick={() => window.print()} data-testid="status-receipt-button"><Download size={14} /> Download receipt</button>
          <Link className="button button-outline auth-submit" to="/" data-testid="status-home-button">Back to home</Link>
        </div>
      </div> : closedAt ? <div className="status-wrap" data-testid="registration-closed">
        <img className="status-logo" src={LOGO_URL} alt="KPL logo" />
        <div className="confirm-ball"><CalendarX size={44} /></div>
        <h2 className="status-title">Registrations Closed</h2>
        <p className="status-sub">Kakinada Premier League — Season 1</p>
        <p className="confirm-note">Player registrations closed on <b>{closedAt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</b>. See you at the player auction — 25 November 2026, Svenska Design Hotel. Follow <a href="https://www.instagram.com/kakinadapremierleague/" target="_blank" rel="noreferrer">@kakinadapremierleague</a> for updates.</p>
        <div className="form-actions">
          <a className="button button-red auth-submit" href="https://www.instagram.com/kakinadapremierleague/" target="_blank" rel="noreferrer" data-testid="closed-insta-link">Follow @kakinadapremierleague</a>
          <Link className="button button-outline auth-submit" to="/" data-testid="closed-home-button">Back to home</Link>
        </div>
      </div> : <>
      <h1>Player registration.</h1>
      <p className="auth-sub">Register for the KPL Season One player pool. Squads are picked at the November 2026 auction.</p>
        {existing && <p className="auth-sub" data-testid="registration-existing-note">You're already registered — submitting again will update your details. <button className="linklike" onClick={() => setShowForm(false)} data-testid="status-back-button">Back to status</button></p>}
        {error && <div className="auth-error" data-testid="registration-error">{error}</div>}
        <form onSubmit={submit}>
          <p className="form-section" data-testid="section-personal">1 · Personal Details</p>
          <div className="auth-field"><label>Player Full Name</label><input type="text" required minLength={2} value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Full name" data-testid="reg-name-input" /></div>
          <div className="auth-field"><label>Father's Name</label><input type="text" required minLength={2} value={form.father_name} onChange={(e) => setField("father_name", e.target.value)} placeholder="Father's name" data-testid="reg-father-input" /></div>
          <div className="form-two">
            <div className="auth-field"><label>Date of Birth</label><input type="date" required value={form.dob} onChange={(e) => setField("dob", e.target.value)} data-testid="reg-dob-input" /></div>
            <div className="auth-field"><label>Age</label><input type="text" value={age} readOnly placeholder="Auto" data-testid="reg-age-input" /></div>
          </div>
          <div className="form-two">
            <div className="auth-field"><label>Gender</label><select required value={form.gender} onChange={(e) => setField("gender", e.target.value)} data-testid="reg-gender-select"><option value="">Select</option><option>Male</option><option>Female</option><option>Other</option></select></div>
            <div className="auth-field"><label>Aadhaar Number</label><input type="text" required minLength={12} maxLength={14} value={form.aadhaar} onChange={(e) => setField("aadhaar", e.target.value.replace(/[^0-9 ]/g, ""))} placeholder="12-digit Aadhaar" data-testid="reg-aadhaar-input" /></div>
          </div>
          <div className="form-two">
            <div className="auth-field"><label>Mobile Number</label><input type="tel" required minLength={10} value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} placeholder="10-digit mobile" data-testid="reg-mobile-input" /></div>
            <div className="auth-field"><label>WhatsApp Number</label><input type="tel" value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} placeholder="If different" data-testid="reg-whatsapp-input" /></div>
          </div>
          <div className="auth-field"><label>Email ID</label><input type="email" value={form.email_id} onChange={(e) => setField("email_id", e.target.value)} placeholder="you@example.com" data-testid="reg-email-input" /></div>
          <div className="form-two">
            <div className="auth-field"><label>District</label><input type="text" required minLength={2} value={form.district} onChange={(e) => setField("district", e.target.value)} placeholder="e.g. East Godavari" data-testid="reg-district-input" /></div>
            <div className="auth-field"><label>City or Village</label><input type="text" required minLength={2} value={form.city} onChange={(e) => setField("city", e.target.value)} placeholder="e.g. Kakinada" data-testid="reg-city-input" /></div>
          </div>

          <p className="form-section" data-testid="section-cricket">2 · Cricket Details</p>
          <div className="form-two">
            <div className="auth-field"><label>Batting Style</label><select value={form.batting_style} onChange={(e) => setField("batting_style", e.target.value)} data-testid="reg-batting-select"><option value="">Select</option><option value="RHB">RHB (Right Hand)</option><option value="LHB">LHB (Left Hand)</option></select></div>
            <div className="auth-field"><label>Bowling Style</label><select value={form.bowling_style} onChange={(e) => setField("bowling_style", e.target.value)} data-testid="reg-bowling-select"><option value="">Select</option>{["RMP", "ROS", "RLS", "LMP", "LOS", "LLS"].map((o) => <option key={o}>{o}</option>)}</select></div>
          </div>
          <div className="form-two">
            <div className="auth-field"><label>All Rounder</label><select value={form.allrounder} onChange={(e) => setField("allrounder", e.target.value)} data-testid="reg-allrounder-select"><option value="">Select</option><option>Batting All Rounder</option><option>Bowling All Rounder</option></select></div>
            <div className="auth-field"><label>Wicket Keeper</label><select required value={form.wicket_keeper} onChange={(e) => setField("wicket_keeper", e.target.value)} data-testid="reg-wk-select"><option value="">Select</option><option>Yes</option><option>No</option></select></div>
          </div>

          <p className="form-section" data-testid="section-experience">3 · Previous Cricket Experience (level — year played)</p>
          <div className="rep-grid">
            {LEVELS.map(([label, key]) => <div className="rep-row" key={key}><span>{label}</span><input type="text" inputMode="numeric" maxLength={4} placeholder="Year" value={form[key]} onChange={(e) => setField(key, e.target.value.replace(/[^0-9]/g, ""))} data-testid={`reg-${key.replace("_", "-")}-input`} /></div>)}
          </div>

          <p className="form-section" data-testid="section-jersey">4 · Dress Measurements</p>
          <div className="auth-field"><label>Jersey Size</label>
            <div className="size-row">{JERSEY_SIZES.map((s) => <button type="button" key={s} className={`size-btn ${form.jersey_size === s ? "active" : ""}`} onClick={() => setField("jersey_size", s)} data-testid={`reg-size-${s}`}>{s}</button>)}</div>
          </div>
          <div className="form-two">
            <div className="auth-field"><label>Jersey Name</label><input type="text" required minLength={2} value={form.jersey_name} onChange={(e) => setField("jersey_name", e.target.value)} placeholder="Name on jersey" data-testid="reg-jersey-name-input" /></div>
            <div className="auth-field"><label>Jersey No.</label><input type="text" required minLength={1} maxLength={3} value={form.jersey_no} onChange={(e) => setField("jersey_no", e.target.value.replace(/[^0-9]/g, ""))} placeholder="Number" data-testid="reg-jersey-no-input" /></div>
          </div>

          <p className="form-section" data-testid="section-documents">5 · Documents Upload</p>
          <UploadBox label="Player Photograph" note="Good quality head shot — front view, not side angles or selfies." value={photoPath} preview={photoPreview} uploading={uploading.photo} onUpload={doUpload("photo", "photo")} testid="reg-photo-input" />
          <UploadBox label="Aadhaar Card" value={aadhaarPath} uploading={uploading.aadhaar} onUpload={doUpload("doc", "aadhaar")} testid="reg-aadhaar-doc-input" />
          <UploadBox label="Date of Birth Proof" value={dobPath} uploading={uploading.dob} onUpload={doUpload("doc", "dob")} testid="reg-dob-doc-input" />

          <p className="form-section" data-testid="section-cricheros">6 · CricHeroes Profile Link</p>
          <div className="auth-field"><label>Profile URL</label><input type="url" required minLength={5} value={form.cricheros_link} onChange={(e) => setField("cricheros_link", e.target.value)} placeholder="https://cricheroes.com/profile/..." data-testid="reg-cricheros-input" /></div>

          <p className="form-section" data-testid="section-declaration">7 · Declaration & Confirmation</p>
          <label className="decl-check" data-testid="reg-declaration-1"><input type="checkbox" required checked={form.declaration} onChange={(e) => setField("declaration", e.target.checked)} /> I confirm that all the information provided is true and correct.</label>
          <label className="decl-check" data-testid="reg-declaration-2"><input type="checkbox" required checked={form.declaration} onChange={(e) => setField("declaration", e.target.checked)} /> I agree to follow all KPL tournament rules and regulations.</label>
          <label className="decl-check" data-testid="reg-declaration-3"><input type="checkbox" required checked={form.declaration} onChange={(e) => setField("declaration", e.target.checked)} /> I understand that registration does not guarantee player selection.</label>

          <div className="form-actions">
            <button className="button button-red auth-submit" type="submit" disabled={busy || uploading.photo || uploading.aadhaar || uploading.dob} data-testid="reg-submit-button">{busy ? "Submitting…" : existing ? "Update registration" : "Submit registration"}</button>
            <button className="button button-outline auth-submit" type="button" onClick={() => { setForm(EMPTY_FORM); setPhotoPath(""); setAadhaarPath(""); setDobPath(""); setPhotoPreview(""); }} data-testid="reg-reset-button"><RotateCcw size={14} /> Reset form</button>
          </div>
        </form>
      </>}
      </>}
    </div>
  </div>;
}
